import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Radio, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { StudioForm } from "@/components/studio/StudioForm";
import {
  useStudios,
  useCreateStudio,
  type Studio,
  type StudioInsert,
} from "@/hooks/useStudios";
import { useBridgeStatus } from "@/hooks/useBridgeConnections";
import { cn } from "@/lib/utils";

function StudioLinkCell({
  children,
  className,
  to,
}: {
  children: ReactNode;
  className?: string;
  to: string;
}) {
  return (
    <TableCell className={cn("p-0", className)}>
      <Link to={to} className="block h-full p-4 text-inherit no-underline">
        {children}
      </Link>
    </TableCell>
  );
}

const StudiosPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const { data: studios = [], isLoading } = useStudios({ search });
  const createStudio = useCreateStudio();

  const handleCreate = (data: StudioInsert) => {
    createStudio.mutate(data, {
      onSuccess: (newStudio) => {
        setIsCreateDialogOpen(false);
        navigate(`/studios/${newStudio.id}`);
      },
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Studios">
        <Button
          offlineEdit
          data-testid="new-studio-button"
          onClick={() => setIsCreateDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          New Studio
        </Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by name, location, number..."
          />
        </div>
      </div>

      <div
        className="border rounded-lg overflow-hidden"
        data-testid="studios-table"
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  Loading studios...
                </TableCell>
              </TableRow>
            ) : studios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Radio className="h-8 w-8" />
                    <p>No studios found</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              studios.map((studio) => (
                <StudioRow key={studio.id} studio={studio} />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="create-studio-dialog">
          <DialogHeader>
            <DialogTitle>Create Studio</DialogTitle>
          </DialogHeader>
          <StudioForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreateDialogOpen(false)}
            isLoading={createStudio.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StudioRow = ({ studio }: { studio: Studio }) => {
  const { data: bridgeStatus, isError } = useBridgeStatus(studio.bridge_url);

  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/50"
      data-testid={`studio-row-${studio.id}`}
    >
      <StudioLinkCell
        to={`/studios/${studio.id}`}
        className="font-mono text-sm"
      >
        {studio.number}
      </StudioLinkCell>
      <StudioLinkCell to={`/studios/${studio.id}`} className="font-medium">
        {studio.name}
      </StudioLinkCell>
      <StudioLinkCell to={`/studios/${studio.id}`}>
        {studio.location || "-"}
      </StudioLinkCell>
      <StudioLinkCell to={`/studios/${studio.id}`}>
        {!studio.bridge_url ? (
          <Badge variant="outline" className="text-muted-foreground">
            No bridge configured
          </Badge>
        ) : isError ? (
          <Badge variant="destructive" className="gap-1">
            <WifiOff className="h-3 w-3" />
            Offline
          </Badge>
        ) : bridgeStatus?.status === "ok" ? (
          <Badge variant="default" className="gap-1 bg-green-600">
            <Wifi className="h-3 w-3" />
            Online ({bridgeStatus.connections.connected}/
            {bridgeStatus.connections.enabled})
          </Badge>
        ) : (
          <Badge variant="secondary" className="gap-1">
            Checking...
          </Badge>
        )}
      </StudioLinkCell>
    </TableRow>
  );
};

export default StudiosPage;
